import { Box } from "@mui/material";
import { v2, nums } from "./v2Tokens";
import { getLabel, NUTRIENT_CATEGORY } from "../../../../enums/FoodDictionaryEnums";
import type { MicronutrientV2 } from "../model/nutritionV2Model";

/**
 * Micronutrients are a REFERENCE TABLE, not a set of stat cards.
 *
 * Twenty coloured boxes made a wall of tiles where the eye had nowhere to rest, and the
 * colour carried no information the category heading wasn't already giving. Two columns of
 * dotted-leader rows — name, leader, value — is how a nutrition label reads, and it lets the
 * trainer find one nutrient instead of scanning all of them.
 */

const UNITS: Record<string, string> = {
  mg: 'מ"ג',
  mcg: 'מק"ג',
  g: "גר׳",
};

const formatAmount = (amount?: number, unit?: string) => {
  if (amount == null) return "—";
  const value = amount >= 10 ? amount.toFixed(1) : amount.toFixed(2);
  const unitHe = UNITS[(unit || "").toLowerCase()] || unit || "";
  return unitHe ? `${value} ${unitHe}` : value;
};

/** Vitamins / minerals / other, grouped by category, for one serving of a food. */
const MicronutrientList = ({ micronutrients }: { micronutrients: MicronutrientV2[] }) => {
  if (!micronutrients.length) {
    return (
      <Box
        sx={{
          fontSize: "12px",
          color: v2.ink2,
          lineHeight: 1.6,
          padding: "12px",
          borderRadius: v2.rSm,
          background: v2.canvas,
          border: `1px solid ${v2.line}`,
        }}
      >
        אין נתוני ויטמינים ומינרלים למזון הזה. הערכים קיימים היום רק במילון המזון של
        המערכת — הם יגיעו לכאן בשלב שינוי מבנה המזונות.
      </Box>
    );
  }

  const grouped = micronutrients.reduce<Record<string, MicronutrientV2[]>>((acc, item) => {
    const category = item.category || "OTHER";
    (acc[category] = acc[category] || []).push(item);
    return acc;
  }, {});

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }}>
      {Object.entries(grouped).map(([category, items]) => (
          <Box key={category}>
            <Box
              sx={{
                fontSize: "12.5px",
                fontWeight: 600,
                color: v2.ink3,
                marginBottom: "4px",
              }}
            >
              {getLabel(NUTRIENT_CATEGORY, category)}
            </Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                columnGap: "28px",
              }}
            >
              {items.map((n) => (
                <Box
                  key={n.code}
                  data-testid="micronutrient"
                  sx={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "8px",
                    padding: "8px 0",
                    borderBottom: `1px solid ${v2.lineSoft}`,
                    fontSize: "13px",
                  }}
                >
                  <Box component="span" sx={{ color: v2.ink2 }}>{n.nameHe || n.nameEn || n.code}</Box>
                  <Box
                    aria-hidden
                    sx={{ flex: 1, borderBottom: "1px dotted #D5DCE3", transform: "translateY(-3px)" }}
                  />
                  <Box component="span" sx={{ fontWeight: 500, whiteSpace: "nowrap", color: v2.ink, ...nums }}>
                    {formatAmount(n.amount, n.unit)}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
      ))}
    </Box>
  );
};

export default MicronutrientList;
